import React from 'react';
import { format, startOfDay } from 'date-fns';
import { RecordStatus } from '../store/slices/recordsSlice';
import { useTranslation } from '../hooks/useTranslation';
import { parseLocalDateString } from '../utils/dateUtils';
import '../styles/HabitList.css';

interface HabitListProps {
  weeklyView: any;
  dates: Date[];
  onRecordClick: (habitId: string, date: string) => void;
  onMonthlyViewClick: (habit: any) => void;
  isUpdating?: boolean;
}

const HabitList: React.FC<HabitListProps> = ({ 
  weeklyView, 
  dates, 
  onRecordClick, 
  onMonthlyViewClick, 
  isUpdating = false 
}) => {
  const { t } = useTranslation();
  
  if (!weeklyView || weeklyView.habits.length === 0) {
    return (
      <div className="empty-state">
        <p>{t('habit.noHabits')}</p>
        <p>{t('habit.addPrompt')}</p>
      </div>
    );
  }

  const getStatusIcon = (status: RecordStatus | null) => {
    switch (status) {
      case 'completed': return '✓';
      case 'skipped': return '→';
      case 'failed': return '✗';
      case 'pending': return '';
      default: return '-';
    }
  };

  const getStatusClass = (status: RecordStatus | null) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'skipped': return 'status-skipped';
      case 'failed': return 'status-failed';
      case 'pending': return 'status-pending';
      default: return 'status-empty';
    }
  };

  const getStreakDisplay = (habit: any): React.ReactNode => {
    if (habit.goalType === 'weekly' && habit.weeklyGoal) {
      const today = startOfDay(new Date());
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() + mondayOffset);
      weekStart.setHours(0, 0, 0, 0);
      
      const currentWeekCompleted = habit.records.filter((r: any) => {
        const recordDate = parseLocalDateString(r.date);
        return recordDate >= weekStart && r.status === 'completed';
      }).length;
      
      return (
        <span className="weekly-goal-display">
          <span className="weekly-progress">
            {currentWeekCompleted}/{habit.weeklyGoal}{t('habit.times')}
          </span>
        </span>
      );
    } else {
      const streak = habit.currentStreak || 0;
      if (streak > 0) {
        return (
          <span className="daily-streak-display">
            <span 
              className={`daily-streak ${
                streak >= 28 ? 'streak-fire' : 
                streak >= 14 ? 'streak-good' : 
                streak >= 7 ? 'streak-start' :
                'streak-basic'
              }`}
            >
              {streak >= 28 && '🔥'}
              {streak}{t('habit.streak')}
            </span>
          </span>
        );
      } else {
        return `0${t('habit.streak')}`;
      }
    }
  };

  return (
    <div className="habit-list">
      {weeklyView.habits.map((habit: any) => (
        <div key={habit.id} className="habit-item">
          <div className="habit-header">
            <div className="habit-info">
              <span className="habit-icon">{habit.icon || '📝'}</span>
              <span className="habit-name">{habit.name}</span>
            </div>
            <div className="habit-actions">
              <span className="habit-streak">
                {getStreakDisplay(habit)}
              </span>
              <button 
                className="monthly-view-btn" 
                title={t('calendar.monthlyView')}
                onClick={() => onMonthlyViewClick(habit)}
              >
                📅
              </button>
            </div>
          </div>
          
          <div className="habit-records">
            {dates.map((date, index) => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const record = habit.records.find((r: any) => r.date === dateStr);
              const status = record?.status || null;
              
              const today = startOfDay(new Date());
              const isFuture = date > today;
              
              const dayOfWeek = date.getDay();
              const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
              const dayKey = dayKeys[dayOfWeek];
              const isDayOff = habit.repeatDays && !habit.repeatDays[dayKey as keyof typeof habit.repeatDays];
              
              if (isDayOff) {
                return (
                  <div key={index} className="record-cell status-dayoff">
                    <span className="status-icon">－</span>
                  </div>
                );
              }
              
              if (isFuture) {
                return (
                  <div key={index} className="record-cell status-disabled">
                    <span className="status-icon">-</span>
                  </div>
                );
              }
              
              return (
                <button
                  key={index}
                  className={`record-cell ${getStatusClass(status)} ${isUpdating ? 'updating' : ''}`}
                  onClick={() => !isUpdating && onRecordClick(habit.id, dateStr)}
                  disabled={isUpdating}
                  style={{
                    backgroundColor: status === 'completed' ? habit.color : undefined,
                  }}
                >
                  <span className="status-icon">{getStatusIcon(status)}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default HabitList;
